import { AlertCircle, Activity, Cloud, Server } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

import { API_BASE_URL, apiClient } from "@/services/api";
import { getAuditUsage, type AuditUsageResponse } from "@/services/audit";

export function DeploymentPanel() {
  const [health, setHealth] = React.useState<{ status: string } | null>(null);
  const [loadingHealth, setLoadingHealth] = React.useState(false);
  const [usage, setUsage] = React.useState<AuditUsageResponse | null>(null);
  const [usageDays, setUsageDays] = React.useState(30);
  const [error, setError] = React.useState<string | null>(null);

  const checkHealth = React.useCallback(async () => {
    setLoadingHealth(true);
    try {
      const response = await apiClient.get("/health");
      setHealth(response.data);
    } catch (err) {
      setHealth(null);
      setError("健康检查失败：后端服务未就绪");
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  const loadUsage = React.useCallback(async () => {
    try {
      const data = await getAuditUsage(usageDays);
      setUsage(data);
    } catch (err) {
      setUsage(null);
      if (err instanceof Error) {
        setError(err.message);
      }
    }
  }, [usageDays]);

  React.useEffect(() => {
    void checkHealth();
    void loadUsage();
  }, [checkHealth, loadUsage]);

  const byAction = usage?.by_action ?? {};
  const bySource = usage?.by_source ?? {};

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Cloud className="h-4 w-4" />
          部署与计费
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 overflow-auto">
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium">服务异常</p>
              <p className="text-xs opacity-90">{error}</p>
            </div>
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-md border p-3">
            <h4 className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Activity className="h-3.5 w-3.5" />
              健康状态
            </h4>
            <div className="flex items-center gap-2">
              <Badge variant={health?.status === "ok" ? "default" : "destructive"}>
                {health?.status || "未知"}
              </Badge>
              <Button size="sm" variant="outline" onClick={() => void checkHealth()} disabled={loadingHealth}>
                {loadingHealth ? "检查中..." : "重新检查"}
              </Button>
            </div>
            <div className="mt-3 space-y-1 text-xs text-muted-foreground">
              <p>前端端口：25000</p>
              <p>后端端口：25001</p>
              <p>API 地址：{API_BASE_URL}</p>
            </div>
          </div>

          <div className="rounded-md border p-3">
            <h4 className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Server className="h-3.5 w-3.5" />
              部署信息
            </h4>
            <p className="text-xs text-muted-foreground">
              当前运行环境：{import.meta.env.MODE}
            </p>
            <p className="text-xs text-muted-foreground">
              生产部署请参考 <code>docs/deployment.md</code>，支持 Docker Compose 与 Kubernetes。
            </p>
          </div>
        </div>

        <div className="rounded-md border p-3">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-medium">租户使用统计</h4>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={365}
                value={usageDays}
                onChange={(e) => setUsageDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
                className="h-7 w-20 text-xs"
              />
              <Button size="sm" variant="outline" onClick={() => void loadUsage()}>
                刷新
              </Button>
            </div>
          </div>
          {usage && (
            <div className="text-xs text-muted-foreground">
              近 {usage.days} 天共 {usage.total} 条审计记录
            </div>
          )}
          <div className="mt-2 grid gap-4 md:grid-cols-2">
            <div>
              <h5 className="mb-1 text-xs font-medium">按操作类型</h5>
              <div className="space-y-1">
                {Object.keys(byAction).length === 0 ? (
                  <p className="text-xs text-muted-foreground">暂无数据</p>
                ) : (
                  Object.entries(byAction).map(([action, count]) => (
                    <div key={action} className="flex items-center justify-between rounded bg-muted px-2 py-1 text-xs">
                      <span>{action}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div>
              <h5 className="mb-1 text-xs font-medium">AI 生成来源</h5>
              <div className="space-y-1">
                {Object.keys(bySource).length === 0 ? (
                  <p className="text-xs text-muted-foreground">暂无数据</p>
                ) : (
                  Object.entries(bySource).map(([source, count]) => (
                    <div key={source} className="flex items-center justify-between rounded bg-muted px-2 py-1 text-xs">
                      <span>{source}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
